// Patch of Heaven - Custom Field Validation
// Hooks into MCF's existing error pattern (.invalid, .active)
// All times evaluated in US Central time

(function () {
  // --- Config ---
  const DELIVERY_CUTOFF_WEEKDAY  = 14; // 2:00 PM CT
  const DELIVERY_CUTOFF_SATURDAY = 10; // 10:00 AM CT
  const PICKUP_CUTOFF_WEEKDAY    = 14; // 2:00 PM CT
  const PICKUP_CUTOFF_SATURDAY   = 11; // 11:00 AM CT

  const SEL = {
    deliveryRadio: 'custom-fields input[type=radio][value=Delivery]',
    pickupRadio:   'custom-fields input[type=radio][value=Pickup]',
    deliveryDate:  'custom-fields input[type=date][data-field-name="Delivery Date"]',
    pickupDate:    'custom-fields input[type=date][data-field-name="Pickup Date"]',
    email:         'custom-fields input[type=text][data-field-name="Your Email"]',
  };

  const shipIcon = `<svg class="ship-icon" viewBox="0 0 14 14" focusable="false" aria-hidden="true"><use href="//patchofheavenboutique.com/cdn/shop/t/3/assets/icon-sprite.svg#packageDelivery"></use></svg>`;

  const initialized = new WeakSet();
  let observer;

  // --- Helpers ---

  const getNowCT = () => {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(now);
    const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    const year = parseInt(p.year), month = parseInt(p.month) - 1, day = parseInt(p.day);
    return {
      year, month, day,
      hour:    parseInt(p.hour),
      minute:  parseInt(p.minute),
      weekday: new Date(Date.UTC(year, month, day)).getUTCDay(),
    };
  };

  const cutoffStr = (hour) =>
    hour >= 12 ? `${hour === 12 ? 12 : hour - 12}:00 PM` : `${hour}:00 AM`;

  const isDateValid = (dateStr, type) => {
    if (!dateStr) return false;
    const now = getNowCT();
    const [y, m, d] = dateStr.split('-').map(Number);
    const selectedInt = y * 10000 + m * 100 + d;
    const todayInt    = now.year * 10000 + (now.month + 1) * 100 + now.day;

    if (selectedInt > todayInt) return true;
    if (selectedInt < todayInt) return false;

    const cutoff = type === 'delivery'
      ? (now.weekday === 6 ? DELIVERY_CUTOFF_SATURDAY : DELIVERY_CUTOFF_WEEKDAY)
      : (now.weekday === 6 ? PICKUP_CUTOFF_SATURDAY   : PICKUP_CUTOFF_WEEKDAY);
    return now.hour < cutoff;
  };

  const getDateError = (dateStr, type) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();

    if (weekday === 0) return 'We are closed on Sundays. Please select another date.';

    if (!isDateValid(dateStr, type)) {
      const now = getNowCT();
      const c = type === 'delivery'
        ? (now.weekday === 6 ? DELIVERY_CUTOFF_SATURDAY : DELIVERY_CUTOFF_WEEKDAY)
        : (now.weekday === 6 ? PICKUP_CUTOFF_SATURDAY   : PICKUP_CUTOFF_WEEKDAY);
      return `Same-day ${type} cutoff is ${cutoffStr(c)} CT. Please select a future date.`;
    }
    return null;
  };

  const showError = (inputEl, message) => {
    const feedback = document.getElementById(inputEl.dataset.fieldFeedback);
    if (!feedback) return;
    const instructions = feedback.querySelector('.field-instructions');
    if (instructions) { instructions.textContent = message; instructions.classList.add('active'); }
    inputEl.classList.add('invalid');
    feedback.classList.add('active');
  };

  const clearError = (inputEl) => {
    const feedback = document.getElementById(inputEl.dataset.fieldFeedback);
    if (!feedback) return;
    const instructions = feedback.querySelector('.field-instructions');
    if (instructions) instructions.classList.remove('active');
    inputEl.classList.remove('invalid');
    feedback.classList.remove('active');
  };

  const isEmailValid = (el) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(el.value.trim());

  const overrideDateMessages = (deliveryDate, pickupDate) => {
    [deliveryDate, pickupDate].forEach(el => {
      if (!el) return;
      const fb = document.getElementById(el.dataset.fieldFeedback);
      const instructions = fb?.querySelector('.field-instructions');
      if (instructions) instructions.textContent = 'We are closed on Sundays. Please select another date.';
    });
  };

  const addDeliveryNote = () => {
    if (document.getElementById('poh-delivery-note')) return;
    const deliveryDate = document.querySelector(SEL.deliveryDate);
    if (!deliveryDate) return;
    const note = document.createElement('p');
    note.id = 'poh-delivery-note';
    note.className = 'poh-field-note';
    note.innerHTML = `At checkout, select ${shipIcon} <strong>Ship</strong> to enter your recipient's delivery address.`;
    deliveryDate.closest('.ma-custom-field').insertAdjacentElement('afterend', note);
  };

  const removeDeliveryNote = () => document.getElementById('poh-delivery-note')?.remove();

  // --- Init ---
  const init = () => {
    const pickupRadio  = document.querySelector(SEL.pickupRadio);
    const deliveryDate = document.querySelector(SEL.deliveryDate);
    const pickupDate   = document.querySelector(SEL.pickupDate);
    const email        = document.querySelector(SEL.email);

    if (!pickupRadio || !deliveryDate || !pickupDate || !email) {
      setTimeout(init, 200);
      return;
    }

    observer?.disconnect();

    // Default to Pickup if neither is selected
    if (!document.querySelector(SEL.deliveryRadio)?.checked && !pickupRadio.checked) {
      pickupRadio.checked = true;
      pickupRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    overrideDateMessages(deliveryDate, pickupDate);

    if (document.querySelector(SEL.deliveryRadio)?.checked) addDeliveryNote();
    else removeDeliveryNote();

    if (!initialized.has(deliveryDate)) {
      initialized.add(deliveryDate);
      deliveryDate.addEventListener('change', () => {
        const err = getDateError(deliveryDate.value, 'delivery');
        if (err) showError(deliveryDate, err); else clearError(deliveryDate);
      });
      deliveryDate.addEventListener('blur', () => {
        const err = getDateError(deliveryDate.value, 'delivery');
        if (err) showError(deliveryDate, err); else clearError(deliveryDate);
      });
    }

    if (!initialized.has(pickupDate)) {
      initialized.add(pickupDate);
      pickupDate.addEventListener('change', () => {
        const err = getDateError(pickupDate.value, 'pickup');
        if (err) showError(pickupDate, err); else clearError(pickupDate);
      });
      pickupDate.addEventListener('blur', () => {
        const err = getDateError(pickupDate.value, 'pickup');
        if (err) showError(pickupDate, err); else clearError(pickupDate);
      });
    }

    if (!initialized.has(email)) {
      initialized.add(email);
      email.addEventListener('blur', () => {
        if (email.value && !isEmailValid(email)) showError(email, 'Please enter a valid email address.');
      });
      email.addEventListener('input', () => clearError(email));
    }

    if (!initialized.has(pickupRadio)) {
      initialized.add(pickupRadio);
      const deliveryRadio = document.querySelector(SEL.deliveryRadio);
      deliveryRadio?.addEventListener('change', addDeliveryNote);
      pickupRadio.addEventListener('change', removeDeliveryNote);
    }

    const form = document.querySelector("form[action='/cart/add']");
    if (form && !initialized.has(form)) {
      initialized.add(form);
      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn && !initialized.has(submitBtn)) {
        initialized.add(submitBtn);
        submitBtn.addEventListener('click', (e) => {
          const deliveryChecked     = document.querySelector(SEL.deliveryRadio)?.checked;
          const pickupChecked       = document.querySelector(SEL.pickupRadio)?.checked;
          const currentDeliveryDate = document.querySelector(SEL.deliveryDate);
          const currentPickupDate   = document.querySelector(SEL.pickupDate);
          const currentEmail        = document.querySelector(SEL.email);
          let blocked = false;

          if (deliveryChecked && currentDeliveryDate?.value) {
            const err = getDateError(currentDeliveryDate.value, 'delivery');
            if (err) { showError(currentDeliveryDate, err); blocked = true; }
          }
          if (pickupChecked && currentPickupDate?.value) {
            const err = getDateError(currentPickupDate.value, 'pickup');
            if (err) { showError(currentPickupDate, err); blocked = true; }
          }
          if (currentEmail?.value && !isEmailValid(currentEmail)) {
            showError(currentEmail, 'Please enter a valid email address.');
            blocked = true;
          }
          if (blocked) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }, true);
      }
    }

    const customFields = document.querySelector('custom-fields');
    if (customFields) {
      let observerTimer;
      observer = new MutationObserver(() => {
        clearTimeout(observerTimer);
        observerTimer = setTimeout(init, 150);
      });
      observer.observe(customFields, { childList: true, subtree: true });
    }
  };

  init();
})();